/**
 * Helper for building the relative URL that resolves a workspace's logo or
 * icon bytes via the Pages Function `functions/api/workspace-assets/[id]/[kind].ts`.
 *
 * Phase A persists the **R2 object key** (e.g. `workspace-1/logo.png`) on
 * `brands.logo_url` / `brands.icon_url`. The browser never reads R2 directly;
 * it fetches `/api/workspace-assets/{id}/{kind}` and the Pages Function
 * looks up the key, gets the object from the (private) `WORKSPACE_ASSETS`
 * bucket, and streams the bytes back. This mirrors how `interview-videos`
 * handles per-segment paths via `functions/api/videos/[sessionId]/[segmentIndex].ts`.
 *
 * **Cache versioning.** A re-upload overwrites the same R2 key, so the bare
 * path is identical pre/post upload. Combined with the Pages Function's
 * long `Cache-Control: max-age=1296000`, the CDN edge would keep serving
 * stale bytes for up to 15 days. We sidestep this by appending a `v` query
 * param derived from the workspace's `updated_at` - the `update_brands_updated_at`
 * trigger bumps it on every row change, so any logo upload (which patches
 * `logo_url` / `icon_url`) yields a new URL that misses the CDN cache. This
 * is the same content-versioning pattern Slack/Notion use for their CDN
 * asset URLs, with the win that we can keep an aggressive long cache and
 * still see uploads land instantly.
 *
 * @module components/workspace/assetUrl
 */

import type { Workspace } from '../../lib/org-types';

/** Asset kind discriminator. */
export type WorkspaceAssetKind = 'logo' | 'icon';

/**
 * Build the relative URL the browser fetches for a workspace's logo or icon.
 * Returns `null` when the workspace has no asset of that kind so callers can
 * branch into a fallback (color dot, initials, etc.) without a broken `<img>`.
 *
 * @param workspace Workspace row. Reads `id`, `logo_url`, `icon_url`, and
 *                  `updated_at` (used as the cache-buster version).
 * @param kind      `'logo'` (wide) or `'icon'` (square).
 */
export function workspaceAssetUrl(
  workspace: Pick<Workspace, 'id' | 'logo_url' | 'icon_url'> &
    Partial<Pick<Workspace, 'updated_at'>>,
  kind: WorkspaceAssetKind,
): string | null {
  const present = kind === 'logo' ? workspace.logo_url : workspace.icon_url;
  if (!present) return null;
  const base = `/api/workspace-assets/${encodeURIComponent(workspace.id)}/${kind}`;
  if (!workspace.updated_at) return base;
  const versionMs = Date.parse(workspace.updated_at);
  if (Number.isNaN(versionMs)) return base;
  return `${base}?v=${Math.floor(versionMs / 1000)}`;
}
